import {
  type LoadingPhase,
  type WithLoadingIndicatorFunction,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { KeyOfTypeContravariant } from '@ipfabric/utils';
import type { Draft } from 'immer';
import isFunction from 'lodash/isFunction';
import type { DraftStateUpdater } from 'utils/react/StateProxy';

/**
 * Handles automatic loading indication for asnyc data fetch
 * 1. Uses unified loading enum for consistency. You can work with simple state === 'LOADING' condition to
 *   simulate boolean variable
 * 2. Changes state to 'ERROR' and rethrows exception to prevent infinite loading
 * Can be used in command:
 * const data = await withLoadingIndication(
 *   (state) => updateState(
 *     draft => draft.loading = state
 *   ),
 *   () => apiRequest(...)
 * )
 * Or in component:
 * const [loadingState, setLoadingState] = useLoadingState();
 * const data = await withLoadingIndication(
 *   setLoadingState,
 *   () => apiRequest(...)
 * )
 */
export const withCommandModelLoadingIndicator = <TState, TResult>(
  cmdContext: { updateState: DraftStateUpdater<TState> },
  propOrUpdater:
    | KeyOfTypeContravariant<TState, LoadingPhase>
    | ((draft: Draft<TState>, phase: LoadingPhase) => void),
): WithLoadingIndicatorFunction<TResult> =>
  withLoadingIndicator((loadingState) => {
    cmdContext.updateState((draft) => {
      if (isFunction(propOrUpdater)) {
        propOrUpdater(draft, loadingState);
      } else {
        // Typescript is unable to typecheck this
        (draft as any)[propOrUpdater] = loadingState;
      }
    });
  });
